import React, { useEffect, useState } from 'react';
import PageHeading from '../../components/PageHeading';
import { MoneyCollectOutlined, UploadOutlined } from '@ant-design/icons';
import {
    Card,
    Select,
    Input,
    Button,
    message,
    Spin,
    Row,
    Col,
    Typography,
    Upload,
    Space,
    Table,
    Tag
} from 'antd';
import axiosInstance from '../../utils/axios';
import CustomerSeatMissingTicket from '../../components/reserve/CustomerSeatMissingTicket';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import '../../pages/booking/MissedTicketGeneration.css';

const { Text } = Typography;

const MissedTicketGeneration = () => {
    const [shows, setShows] = useState([]);
    const [selectedShow, setSelectedShow] = useState(null);
    const [screen, setScreen] = useState({});
    const [selectedReservedSeats, setSelectedReservedSeats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState("");
    const [title, setTitle] = useState("");
    const [remark, setRemark] = useState("");
    const [amount, setAmount] = useState("");
    const [fileList, setFileList] = useState([]);

    useEffect(() => {
        fetchShows();
    }, []);

    const fetchShows = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get("missed-ticket/shows-mini/");
            setShows(response.data || []);
        } catch (err) {
            message.error("Failed to load shows");
            console.error(err);
            setShows([]);
        } finally {
            setLoading(false);
        }
    };

    const handleShowSelect = async (valueKey) => {
        if (!valueKey) {
            setSelectedShow(null);
            setScreen({});
            setSelectedReservedSeats([]);
            return;
        }

        const show = shows.find(s =>
            `${s.show_id}-${s.show_date_id}-${s.show_time_slot_id}` === valueKey
        );

        if (!show) {
            message.error("Show not found");
            return;
        }

        setSelectedShow(show);
        setSelectedReservedSeats([]);
        setScreen({});

        setLoading(true);
        try {
            const { data } = await axiosInstance.get(
                `booking/screen/${show.show_id}/theatre/${show.theatre_id}/screen/${show.screen_id}/date/${show.show_date_id}/slot/${show.show_time_slot_id}/`
            );

            setScreen({
                sections: data?.screen?.sections || [],
                bookedSeats: data?.bookedSeats || [],
                reservedSeats: [],
                reservation: data?.reservation || []
            });
        } catch (err) {
            message.error("Failed to load seat layout");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (selectedReservedSeats.length === 0) {
            message.error("Select at least one reserved seat to convert");
            return;
        }

        if (!email) {
            message.error("Customer email is required");
            return;
        }

        const formData = new FormData();
        formData.append("show_id", selectedShow.show_id);
        formData.append("show_date_id", selectedShow.show_date_id);
        formData.append("show_timeslot_id", selectedShow.show_time_slot_id);
        formData.append("email", email);
        formData.append("title", title);
        formData.append("remark", remark);
        formData.append("amount", amount || 0);

        selectedReservedSeats.forEach(seat => {
            formData.append("seats[]", JSON.stringify({ id: seat.seat_id }));
        });

        if (fileList.length > 0) {
            formData.append("file", fileList[0].originFileObj);
        }

        try {
            setLoading(true);
            const { data } = await axiosInstance.post("missed-ticket/generate/", formData);
            message.success("Recovered ticket created and email sent");
            setSelectedReservedSeats([]);
            setEmail("");
            setTitle("");
            setRemark("");
            setAmount("");
            setFileList([]);
            setSelectedShow(null);
            setScreen({});
        } catch (err) {
            message.error("Failed to generate ticket");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const generatePdf = () => {
        if (!selectedShow || selectedReservedSeats.length === 0) {
            message.error("Please select a show and at least one seat");
            return;
        }
        // Validate email even for PDF only
        if (!email.trim()) {
            message.error("Customer email is required for PDF generation");
            return;
        }

        if (!selectedShow || selectedReservedSeats.length === 0) {
            message.error("Please select a show and at least one seat");
            return;
        }
        const doc = new jsPDF();
        doc.setFontSize(18);
        doc.text("Recovered Ticket — Manual Generation", 14, 22);

        doc.setFontSize(11);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 32);

        doc.setFontSize(14);
        doc.text("Show Details", 14, 45);
        doc.setFontSize(11);
        doc.text(`Movie: ${selectedShow.movie_name}`, 14, 55);
        doc.text(`Theatre: ${selectedShow.theatre_name}`, 14, 63);
        doc.text(`Screen: ${selectedShow.screen_name}`, 14, 71);
        doc.text(`Date: ${new Date(selectedShow.date).toLocaleDateString('en-IN')}`, 14, 79);
        doc.text(`Timeslot: ${selectedShow.timeslot_title || 'N/A'} (${selectedShow.timeslot_start} - ${selectedShow.timeslot_end})`, 14, 87);

        let y = 105;
        if (email) { doc.text(`Customer Email: ${email}`, 14, y); y += 8; }
        if (title) { doc.text(`Title: ${title}`, 14, y); y += 8; }
        if (remark) { doc.text(`Remark: ${remark}`, 14, y); y += 8; }
        if (amount) { doc.text(`Amount: ${amount}`, 14, y); y += 8; }

        const seatData = selectedReservedSeats.map(s => [
            s.section_name || '-',
            s.block_name || '-',
            s.row_name || '-',
            s.seat_label || '-',
            s.price ? `${s.price}` : '-',
            s.booking_id || '-'
        ]);

        autoTable(doc, {
            startY: y + 10,
            head: [['Section', 'Block', 'Row', 'Seat', 'Price', 'Booking ID']],
            body: seatData,
            theme: 'grid',
        });

        doc.save(`Recovered_Ticket_${selectedShow.movie_name.replace(/ /g, '_')}_${selectedShow.date}.pdf`);
    };

    // Table for selected reserved seats
    const selectedSeatsColumns = [
        { title: 'Section', dataIndex: 'section_name', key: 'section' },
        { title: 'Block', dataIndex: 'block_name', key: 'block' },
        { title: 'Row', dataIndex: 'row_name', key: 'row' },
        { title: 'Seat', dataIndex: 'seat_label', key: 'seat' },
        { title: 'Price', dataIndex: 'price', key: 'price', render: p => p ? `${p}` : '-' },
        { title: 'Booking ID', dataIndex: 'booking_id', key: 'booking_id', render: id => id || '-' },
    ];

    return (
        <div className="dashboard">
            <PageHeading name="Reserve to Ticket Generator" icon={<MoneyCollectOutlined />} />

            <Card title="Select Show" style={{ marginBottom: 24, paddingBottom: 50 }}>
                <Select
                    showSearch
                    placeholder="Search and select show"
                    optionFilterProp="children"
                    onChange={handleShowSelect}
                    allowClear
                    loading={loading}
                    style={{ width: '100%' }}
                    notFoundContent={loading ? <Spin size="small" /> : "No shows available"}
                    className="tall-select"
                >
                    {shows.map(show => {
                        const valueKey = `${show.show_id}-${show.show_date_id}-${show.show_time_slot_id}`;

                        return (
                            <Select.Option key={valueKey} value={valueKey}>
                                <div style={{ lineHeight: '1.4' }}>
                                    <div style={{ fontWeight: '600', fontSize: '15px', color: '#1890ff' }}>
                                        {show.movie_name} ({show.movie_lang})
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#666' }}>
                                        {show.theatre_name} • {show.screen_name} • {new Date(show.date).toLocaleDateString('en-IN')} • {show.timeslot_title || 'Show'} ({show.timeslot_start} - {show.timeslot_end})
                                    </div>
                                </div>
                            </Select.Option>
                        );
                    })}
                </Select>
            </Card>

            {selectedShow && Object.keys(screen).length > 0 && (
                <>
                    <Card title="Selected Show Details" style={{ marginBottom: 24 }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Text strong>Movie: </Text>{selectedShow.movie_name} ({selectedShow.movie_lang})<br />
                                <Text strong>Theatre: </Text>{selectedShow.theatre_name}<br />
                                <Text strong>Screen: </Text>{selectedShow.screen_name}
                            </Col>
                            <Col span={12}>
                                <Text strong>Date: </Text>{new Date(selectedShow.date).toLocaleDateString('en-IN')}<br />
                                <Text strong>Timeslot: </Text>{selectedShow.timeslot_title || 'N/A'} ({selectedShow.timeslot_start} - {selectedShow.timeslot_end})
                            </Col>
                        </Row>
                    </Card>

                    <CustomerSeatMissingTicket
                        reservations={screen.reservation || []}
                        sections={screen.sections || []}
                        selected={[]}
                        bookedSeats={screen.bookedSeats || []}
                        onToggle={() => { }}
                        selectedReserved={selectedReservedSeats}
                        onReservedToggle={setSelectedReservedSeats}
                    />

                    {/* Selected Reserved Seats Table */}
                    {selectedReservedSeats.length > 0 && (
                        <Card title={`Selected Reserved Seats (${selectedReservedSeats.length})`} style={{ marginTop: 24 }}>
                            <Table
                                dataSource={selectedReservedSeats}
                                columns={selectedSeatsColumns}
                                rowKey="seat_id"
                                pagination={false}
                                size="small"
                            />
                        </Card>
                    )}
                </>
            )}

            {selectedReservedSeats.length > 0 && (
                <Card title="Generate Recovered Ticket" style={{ marginTop: 24 }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Text strong>Customer Email <span style={{ color: 'red' }}>*</span></Text>
                            <Input
                                placeholder="customer@example.com"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                style={{ marginTop: 8 }}
                            />
                        </Col>
                        <Col span={12}>
                            <Text strong>Title (optional)</Text>
                            <Input
                                placeholder="e.g., Missed Online Payment"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                style={{ marginTop: 8 }}
                            />
                        </Col>
                    </Row>

                    <Row gutter={16} style={{ marginTop: 16 }}>
                        <Col span={12}>
                            <Text strong>Amount ()</Text>
                            <Input
                                type="number"
                                placeholder="0.00"
                                value={amount}
                                onChange={e => setAmount(e.target.value)}
                                style={{ marginTop: 8 }}
                            />
                        </Col>
                        <Col span={12}>
                            <Text strong>Supporting Document (optional)</Text>
                            <Upload
                                fileList={fileList}
                                onChange={({ fileList: newFileList }) => setFileList(newFileList.slice(-1))}
                                beforeUpload={() => false}
                                maxCount={1}
                                style={{ marginTop: 8 }}
                            >
                                <Button icon={<UploadOutlined />}>Upload File</Button>
                            </Upload>
                        </Col>
                    </Row>

                    <Row style={{ marginTop: 16 }}>
                        <Col span={24}>
                            <Text strong>Remark</Text>
                            <Input.TextArea
                                rows={3}
                                placeholder="Additional notes..."
                                value={remark}
                                onChange={e => setRemark(e.target.value)}
                                style={{ marginTop: 8 }}
                            />
                        </Col>
                    </Row>

                    <Space style={{ marginTop: 24 }}>
                        <Button type="primary" onClick={handleSubmit} loading={loading}>
                            Convert & Send Email
                        </Button>
                        <Button onClick={generatePdf}>
                            Generate PDF Only
                        </Button>
                    </Space>
                </Card>
            )}
        </div>
    );
};

export default MissedTicketGeneration;